import { FeatureFlag } from '@ps/web-core-enums';
import { takeUntil } from 'rxjs/operators';

import type { WidgetEffectParams } from '@swf/core/store';
import { authChange } from '@swf/core/utils/actions';

import type { SportsPromotionGridWidgetConfig } from '../config';
import type { CrmResponse, IceResponse } from '../types';

import type { WidgetDispatch, WidgetSelect } from './types';

export const effects = {
  async preload(
    _payload: undefined,
    {
      actions,
      dispatch,
      rootState,
      select,
      widget
    }: WidgetEffectParams<SportsPromotionGridWidgetConfig>
  ) {
    const {
      crmPromotionsRequested,
      icePromotionsRequested,
      icePromotionsCleared,
      preloaded,
      clear
    }: WidgetDispatch = dispatch[widget.uid];
    const { hasPreloaded }: WidgetSelect = select[widget.uid];

    const authChangeAction = authChange(actions);

    if (authChangeAction) {
      authChangeAction.pipe(takeUntil(widget.unmount)).subscribe({
        next: ({ type }) => {
          switch (type) {
            case 'session/clear':
              icePromotionsCleared();
              break;
            default:
              icePromotionsRequested();
          }

          crmPromotionsRequested();
        },
        complete: () => clear()
      });
    }

    if (!hasPreloaded(rootState)) {
      await Promise.allSettled([crmPromotionsRequested(), icePromotionsRequested()]);
      preloaded();
    }
  },

  async crmPromotionsRequested(
    _payload: undefined,
    {
      dispatch,
      request,
      rootState,
      widget,
      select
    }: WidgetEffectParams<SportsPromotionGridWidgetConfig>
  ) {
    const zoneId = 'ISPPROMOTIONS';
    const { crmPromotionsLoaded, crmPromotionsFailed }: WidgetDispatch = dispatch[widget.uid];

    if (!select.sports.isEligibleForPromotions(rootState)) {
      crmPromotionsLoaded();
      return;
    }

    try {
      const { body } = await request<CrmResponse>({
        service: 'crm.zone',
        body: { zoneId }
      });
      crmPromotionsLoaded(body.contents);
    } catch (error) {
      crmPromotionsFailed();
    }
  },

  async icePromotionsRequested(
    _payload: undefined,
    {
      dispatch,
      request,
      widget,
      select,
      rootState
    }: WidgetEffectParams<SportsPromotionGridWidgetConfig>
  ) {
    const { icePromotionsLoaded, icePromotionsFailed }: WidgetDispatch = dispatch[widget.uid];
    const isIcePromotionsEnabled = select.feature.check(rootState, FeatureFlag.ISPICEPromotions);

    if (select.sports.isEligibleForPromotions(rootState) && isIcePromotionsEnabled) {
      try {
        const { body } = await request<IceResponse>({
          service: '/api/v1-preview/ice/userupdates'
        });

        icePromotionsLoaded(body);
      } catch (error) {
        icePromotionsFailed();
      }
    }
  }
};
